import React from 'react';
import axiosPrivate from 'config/axiosPrivate';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { fetchAdminClientTextOrderDisplay as fetchAdminTextOrderDisplay } from 'store/AdminClient/OrderDisplay/features';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  UncontrolledTooltip,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { utils as xlsxUtils, writeFile as xlsxWriteFile, read as xlsxRead } from 'xlsx';
import { transformToSemanticGap } from 'helpers/FormatKeywordsHelper';
import { addRedaction, setCurrentRedaction } from 'store/AdminClient/RedactionData';
import './RedactionModal.css';
import GapGenerationModal from 'components/shared/modal/GapGenerationModal';
import SimpleNotification from 'components/shared/Notification/SimpleNotification';
import ProjectCreatedModal from 'components/shared/modal/ProjectCreatedModal';
import BuyCreditsModal from 'components/shared/BuyCreditsModal';
import {
  buildUnassignedGenerationFormData,
  getMultipartRequestConfig,
} from 'utils/smartContent/smartContentGenerationUtils';

const WORD_MIME_TYPES = new Set([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const MAX_KNOWLEDGE_BASE_FILES = 3;
const KNOWLEDGE_BASE_ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md', '.csv'];
const KNOWLEDGE_BASE_ACCEPT =
  '.pdf,.docx,.txt,.md,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,text/csv';

const getFileExtension = (fileName = '') => {
  const match = String(fileName).toLowerCase().match(/\.[^.]+$/);
  return match ? match[0] : '';
};

const isAcceptedKnowledgeBaseFile = (file) =>
  KNOWLEDGE_BASE_ACCEPTED_EXTENSIONS.includes(getFileExtension(file?.name));

const isWordDocumentFile = (file) => {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  return (
    name.endsWith('.doc') ||
    name.endsWith('.docx') ||
    WORD_MIME_TYPES.has(file.type)
  );
};

const mergeWordFiles = (existingFiles, incomingFiles) => [
  ...existingFiles,
  ...incomingFiles.filter(
    (incoming) =>
      !existingFiles.some(
        (existing) =>
          existing.name === incoming.name && existing.lastModified === incoming.lastModified
      )
  ),
];

const isExcelDocumentFile = (file) => {
  if (!file) return false;
  const allowedTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
  ];
  const name = String(file.name || '').toLowerCase();
  return (
    allowedTypes.includes(file.type) ||
    name.endsWith('.csv') ||
    name.endsWith('.xlsx') ||
    name.endsWith('.xls')
  );
};

const RedactionModal = ({
  isOpen,
  onClose,
  orderId,
  orderSiloName,
  orderWebsite,
  language,
  country,
  maxWords,
  orderType,
  orderTextType,
  title,
  aiAgentId,
  orderTextAmount,
  initialKeyword = '',
  t = (key) => key,
  generationContext = 'AC',
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [formData, setFormData] = React.useState({
    keyword: '',
    url: '',
    semanticGap: '',
    internalLinking: '',
    writingAxis: '',
    type: '',
    textToTranslate: '',
  });
  const [errors, setErrors] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRetrieving, setIsRetrieving] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState('');
  const [alertType, setAlertType] = React.useState('danger');
  const [keywords, setKeywords] = React.useState('');
  const [combinedKeywords, setCombinedKeywords] = React.useState('');
  const [combinedKeywordsList, setCombinedKeywordsList] = React.useState([]);
  const [websites, setWebsites] = React.useState('');
  const [isReadyToRedirect, setIsReadyToRedirect] = React.useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = React.useState('');
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [errorSuccess, setErrorSuccess] = React.useState(false);
  const [previousStatuses, setPreviousStatuses] = React.useState(new Set());
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [selectedOption, setSelectedOption] = React.useState('option1');
  const [activeTab, setActiveTab] = React.useState('tab1');
  const [filePreviewData, setFilePreviewData] = React.useState([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 5;
  const [createdRedactionId, setCreatedRedactionId] = React.useState(null);
  const [currentGeneratedTextsCount, setCurrentGeneratedTextsCount] = React.useState(0);
  const [isGapModalOpen, setIsGapModalOpen] = React.useState(false);
  const [gapMode, setGapMode] = React.useState('aqisis');
  const [gapAnalysisEnabled, setGapAnalysisEnabled] = React.useState(null);
  const [remainingCredits, setRemainingCredits] = React.useState(null);
  const [isCreditsLimitModalOpen, setIsCreditsLimitModalOpen] = React.useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = React.useState(false);
  const [isContentLimitModalOpen, setIsContentLimitModalOpen] = React.useState(false);
  const [isKnowledgeBaseSectionOpen, setIsKnowledgeBaseSectionOpen] = React.useState(false);
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = React.useState([]);
  const [knowledgeBaseFileError, setKnowledgeBaseFileError] = React.useState('');

  const normalizedOrderType = ((orderType || '') + '').toUpperCase();
  const normalizedOrderTextType = ((orderTextType || '') + '').toUpperCase();
  const translationIndicators = new Set(['TRANSLATION', 'TRANSLATED', 'EDITORIAL_AND_TRANSLATION']);

  const isSmart = normalizedOrderType.startsWith('SMART');
  const isTranslationProject = translationIndicators.has(normalizedOrderTextType);
  const [pollingError, setPollingError] = React.useState(null);
  const [jobStatuses, setJobStatuses] = React.useState([]);
  const [fetchedJobs, setFetchedJobs] = React.useState(new Set());
  const pollingIntervalRef = React.useRef(null);
  const normalizedGenerationContext = (generationContext || 'AC').toUpperCase();
  const generationStorageKey = orderId ? `${normalizedGenerationContext}_Generation_${orderId}` : null;
  const generationEventName = `${normalizedGenerationContext.toLowerCase()}-generation-changed`;

  const [isDragging, setIsDragging] = React.useState(false);
  const [notificationMessage, setNotificationMessage] = React.useState('');
  const notificationTimeoutRef = React.useRef(null);
  const selectedFilesRef = React.useRef([]);

  React.useEffect(() => {
    selectedFilesRef.current = selectedFiles;
  }, [selectedFiles]);


  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const validateBatchFileCount = (fileCount) => {
    if (!orderTextAmount || fileCount <= 0) return true;
    const remainingTexts = orderTextAmount - currentGeneratedTextsCount;
    if (fileCount > remainingTexts) {
      setErrors({
        file: t(`Actuellement, le fichier comprend ${fileCount} textes ; il reste cependant ${remainingTexts} texte(s) à générer.`),
      });
      setAlertMessage(t(`Vous avez atteint la limite de textes pour ce projet. Il ne reste que ${remainingTexts} texte(s) à générer.`));
      setAlertType('danger');
      return false;
    }
    return true;
  };

  const processWordFiles = (files, { append } = {}) => {
    const incomingFiles = Array.from(files || []).filter(Boolean);
    if (incomingFiles.length === 0) return;

    const invalidFiles = incomingFiles.filter((file) => !isWordDocumentFile(file));
    if (invalidFiles.length > 0) {
      setAlertMessage(t('Format de fichier non supporté. Veuillez sélectionner un fichier .doc ou .docx'));
      setAlertType('danger');
      return;
    }

    const shouldAppend = append ?? selectedFilesRef.current.length > 0;
    const mergedFiles = shouldAppend
      ? mergeWordFiles(selectedFilesRef.current, incomingFiles)
      : incomingFiles;

    if (!validateBatchFileCount(mergedFiles.length)) {
      if (!shouldAppend) {
        setSelectedFiles([]);
      }
      return;
    }

    setSelectedFiles(mergedFiles);
    setSelectedFile(null);
    setFilePreviewData([]);
    setSelectedOption('option2');
    setAlertMessage('');
    setCurrentPage(1);
    setErrors({});
  };

  const processExcelFile = (file) => {
    if (!isExcelDocumentFile(file)) {
      setAlertMessage(t('Format de fichier non supporté. Veuillez sélectionner un fichier .xlsx, .xls ou .csv'));
      setAlertType('danger');
      return;
    }

    setSelectedFile(file);
    setSelectedFiles([]);
    setSelectedOption('option2');
    setAlertMessage('');
    setCurrentPage(1);
    setErrors({});

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = xlsxRead(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = xlsxUtils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length > 1) {
          const rows = jsonData.slice(1).filter((row) => row.length > 0 && row[0]);

          if (!validateBatchFileCount(rows.length)) {
            setSelectedFile(null);
            setFilePreviewData([]);
            return;
          }

          const formattedData = rows.map((row) => ({
            keyword: row[0] || '',
            url: row[1] || '',
            gap: row[2] || '',
            maillage: row[3] || '',
            writingAxis: row[4] || '',
          }));

          setFilePreviewData(formattedData);

          if (notificationTimeoutRef.current) {
            clearTimeout(notificationTimeoutRef.current);
          }
          setNotificationMessage(t('Modèle Excel téléchargé !'));
          notificationTimeoutRef.current = setTimeout(() => {
            setNotificationMessage('');
          }, 3000);
        }
      } catch (error) {
        console.error('Erreur lors de la lecture du fichier:', error);
        setAlertMessage(t('Erreur lors de la lecture du fichier'));
        setAlertType('danger');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    if (isTranslationProject) {
      processWordFiles(files);
      return;
    }

    processExcelFile(files[0]);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setSelectedFiles([]);
    setFilePreviewData([]);
    setCurrentPage(1);
    setErrors({});
    setAlertMessage('');
    const fileInput = document.getElementById('smartBatchUpload');
    if (fileInput) fileInput.value = '';
  };

  const handleRemoveWordFile = (fileToRemove) => {
    setSelectedFiles((prev) => {
      const nextFiles = prev.filter(
        (file) =>
          file.name !== fileToRemove.name || file.lastModified !== fileToRemove.lastModified
      );
      if (nextFiles.length === 0) {
        const fileInput = document.getElementById('smartBatchUpload');
        if (fileInput) fileInput.value = '';
      }
      return nextFiles;
    });
    setErrors({});
    setAlertMessage('');
  };


  const setGenerationStatus = (isGenerating) => {
    if (!generationStorageKey) return;
    try {
      window.localStorage.setItem(generationStorageKey, isGenerating ? '1' : '0');
      if (isGenerating) {
        window.localStorage.removeItem(`${generationStorageKey}_error`);
      }
      window.dispatchEvent(new Event(generationEventName));
    } catch { }
  };

  const checkJobStatus = async () => {
    try {
      const response = await axiosPrivate.get(`/project/job-status/${orderId}`);

      if (response.status === 204) {
        try {
          if (
            window.localStorage.getItem('AdminClient_ProjectDisplay_id') ==
            orderId
          ) {
            await dispatch(fetchAdminTextOrderDisplay(orderId));
          }
          setPollingError(null);
        } catch {
          setPollingError('Erreur lors de la récupération des textes');
        } finally {
          stopPolling();
        }
        return;
      }
      const currentStatuses = response.data || [];
      setJobStatuses(currentStatuses);

      const generationFailed =
        currentStatuses.length > 0 &&
        currentStatuses.every(
          (job) => String(job?.jobStatus || '').trim().toUpperCase() === 'FAILED'
        );
      if (generationFailed) {
        try {
          if (generationStorageKey) {
            window.localStorage.setItem(
              `${generationStorageKey}_error`,
              `${t('La génération a échoué.')}\n${t('Veuillez vérifier vos données d’entrée et réessayer. Si le problème persiste, contactez le support.')}`
            );
          }
        } catch { }
        setGenerationStatus(false);
        return;
      }

      const newCompletedJobs = currentStatuses.filter(
        job => job.jobStatus === 'COMPLETED' && !fetchedJobs.has(job.keyword)
      );
      if (newCompletedJobs.length > 0) {
        try {
          if (
            window.localStorage.getItem('AdminClient_ProjectDisplay_id') ==
            orderId
          ) {
            await dispatch(fetchAdminTextOrderDisplay(orderId));
          }

          setFetchedJobs(prev => {
            const updated = new Set(prev);
            newCompletedJobs.forEach(job => updated.add(job.keyword));
            return updated;
          });
          setPollingError(null);
        } catch {
          setPollingError('Erreur lors de la récupération des textes');
        }
      }
    } catch (error) {
      setPollingError('Erreur lors de la surveillance des jobs');
    }
  };

  const startPolling = () => {
    if (pollingIntervalRef.current) return;
    checkJobStatus();
    setGenerationStatus(true);
    pollingIntervalRef.current = setInterval(checkJobStatus, 10000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    try {
      if (generationStorageKey) {
        window.localStorage.removeItem(`${generationStorageKey}_error`);
      }
    } catch { }
    setGenerationStatus(false);
    setNotificationMessage('');
  };

  const getExcelColumnWidth = (header, minWidth = 25) => ({
    wch: Math.max(String(header).length + 10, minWidth),
  });

  const handleDownloadTemplate = () => {
    try {
      const workBook = xlsxUtils.book_new();
      let headersData, columnWidths, fileName;

      if (isTranslationProject) {
        const keywordHeader = t('Mot clé (Obligatoire)');
        const urlHeader = t('Url page (Optionnel)');
        const textHeader = t('Texte à traduire');
        headersData = [
          {
            [keywordHeader]: '',
            [urlHeader]: '',
            [textHeader]: ''
          }
        ];
        columnWidths = [
          getExcelColumnWidth(keywordHeader),
          getExcelColumnWidth(urlHeader),
          getExcelColumnWidth(textHeader, 40),
        ];
        fileName = 'modele_traduction_smart.xlsx';
      } else {
        const keywordHeader = t('Mots cle (Obligatoire)');
        const urlHeader = t('Url (Optionnel)');
        const gapHeader = t('Gap semantique (Optionnel)');
        const maillageHeader = t('Maillage interne (Optionnel)');
        const axeHeader = t('Axe de rédaction (Optionnel)');
        headersData = [
          {
            [keywordHeader]: '',
            [urlHeader]: '',
            [gapHeader]: '',
            [maillageHeader]: '',
            [axeHeader]: ''
          }
        ];
        columnWidths = [
          getExcelColumnWidth(keywordHeader),
          getExcelColumnWidth(urlHeader),
          getExcelColumnWidth(gapHeader, 35),
          getExcelColumnWidth(maillageHeader, 35),
          getExcelColumnWidth(axeHeader, 35),
        ];
        fileName = 'modele_redaction_smart.xlsx';
      }

      const xlsxSheet = xlsxUtils.json_to_sheet(headersData);
      xlsxSheet['!cols'] = columnWidths;
      xlsxUtils.book_append_sheet(workBook, xlsxSheet, 'Template');
      xlsxWriteFile(workBook, fileName);
    } catch (e) {
      console.error('Template download failed:', e);
    }
  };

  const fetchCurrentTextsCount = React.useCallback(async () => {
    if (!orderId || !orderTextAmount) return;
    try {
      const response = await axiosPrivate.get(`/texts/external/by-project/${orderId}`);
      if (response.status === 200) {
        const texts = response.data || [];
        setCurrentGeneratedTextsCount(texts.length);
      }
    } catch (error) {
      console.error('Error fetching texts count:', error);
    }
  }, [orderId, orderTextAmount]);

  React.useEffect(() => {
    if (isOpen && orderId) {
      fetchCurrentTextsCount();
    }
  }, [isOpen, orderId, fetchCurrentTextsCount]);

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
      const keyword = String(initialKeyword || '').trim();
      if (keyword) {
        setFormData((prev) => ({ ...prev, keyword }));
      }
    }
  }, [isOpen, initialKeyword]);

  React.useEffect(() => {
    let isMounted = true;

    const fetchCompanyQuotas = async () => {
      try {
        const { data } = await axiosPrivate.get('/project/company-quotas');
        if (!isMounted) return;
        setGapAnalysisEnabled(data?.gapAnalysisEnabled === true);

        const apiRemaining = Number(
          data?.remainingCredits ?? data?.availableCredits ?? data?.credits
        );
        if (Number.isFinite(apiRemaining)) {
          setRemainingCredits(Math.max(apiRemaining, 0));
        } else {
          const total = Number(data?.totalCredits);
          const used = Number(data?.usedCredits);
          if (Number.isFinite(total) && Number.isFinite(used)) {
            setRemainingCredits(Math.max(total - used, 0));
          } else {
            setRemainingCredits(null);
          }
        }
      } catch (error) {
        if (isMounted) {
          setGapAnalysisEnabled(false);
          setRemainingCredits(null);
        }
      }
    };

    fetchCompanyQuotas();
    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!generationStorageKey) return;

    const syncFromStorage = () => {
      try {
        const val = window.localStorage.getItem(generationStorageKey);
        if (val === '1') {
          setNotificationMessage(t('Génération en cours...'));
        } else {
          setNotificationMessage('');
        }
      } catch { }
    };

    syncFromStorage();

    const handler = () => syncFromStorage();
    window.addEventListener(generationEventName, handler);

    return () => {
      window.removeEventListener(generationEventName, handler);
    };
  }, [generationStorageKey, generationEventName, t]);

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (isTranslationProject) {
      processWordFiles(files);
      e.target.value = '';
      return;
    }

    processExcelFile(files[0]);
    e.target.value = '';
  };

  React.useEffect(() => {
    if (isReadyToRedirect) {
      const isConsultant = window.location.pathname.startsWith('/Consultant');
      if (isSmart) {
        history.push(
          isConsultant
            ? `/Consultant/DetailsProject?id=${orderId}`
            : `/AdminClient/DetailsProject?id=${orderId}`
        );
      } else {
        const isWriterClient = window.location.pathname.includes('/WriterClient/');
        if (isWriterClient) {
          history.push('/WriterClient/TextDetails');
        } else if (isConsultant) {
          history.push('/Consultant/TextDetails');
        } else {
          history.push('/AdminClient/TextDetails');
        }
      }
      setIsReadyToRedirect(false);
    }
  }, [isReadyToRedirect, history, orderId, isSmart]);


  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const normalizeUrl = (input) => {
    return String(input || '')
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '');
  };

  const checkPremiumWebsiteQuota = async () => {
    if (isSmart) return true;
    const normalizedOrderWebsite = normalizeUrl(orderWebsite);
    if (!normalizedOrderWebsite) return true;

    try {
      const websitesResponse = await axiosPrivate.get('/project/website/all');
      const websites = Array.isArray(websitesResponse?.data) ? websitesResponse.data : [];
      const matchingWebsite = websites.find((websiteItem) => {
        const websiteUrl = normalizeUrl(websiteItem?.urlSite || websiteItem?.url || websiteItem?.name || '');
        return websiteUrl === normalizedOrderWebsite;
      });

      if (!matchingWebsite?.id) return true;

      const quotasResponse = await axiosPrivate.get(`/project/website-quotas/${matchingWebsite.id}`);
      const maxTexts = Number(quotasResponse?.data?.maxTexts ?? 0);
      const usedTexts = Number(quotasResponse?.data?.usedTexts ?? 0);

      return maxTexts - usedTexts > 0;
    } catch {
      // Keep existing behavior if quotas endpoint fails unexpectedly.
      return true;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name && value && selectedOption === 'option2') {
      setSelectedOption('option1');
      setSelectedFile(null);
      setSelectedFiles([]);
      setSelectedFiles([]);
      const fileInput = document.getElementById('smartBatchUpload');
      if (fileInput) fileInput.value = '';
    }
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (alertMessage) setAlertMessage('');
  };

  const handleKnowledgeBaseFileChange = (e) => {
    const input = e.target;
    const files = Array.from(input?.files || []);
    if (input && typeof input.value === 'string') {
      input.value = '';
    }
    if (!files.length) return;

    const validFiles = files.filter(isAcceptedKnowledgeBaseFile);
    if (validFiles.length !== files.length) {
      setKnowledgeBaseFileError(
        t('Format de fichier non supporté. Formats acceptés : PDF, DOCX, TXT, MD, CSV.')
      );
    }

    if (!validFiles.length) return;

    const remaining = MAX_KNOWLEDGE_BASE_FILES - knowledgeBaseFiles.length;

    if (remaining <= 0 || validFiles.length > remaining) {
      setKnowledgeBaseFileError(t('Vous pouvez importer 3 fichiers maximum.'));
    } else if (validFiles.length === files.length) {
      setKnowledgeBaseFileError('');
    }

    if (remaining <= 0) return;

    const acceptedFiles = validFiles.slice(0, remaining);
    setKnowledgeBaseFiles((prev) => [...prev, ...acceptedFiles]);
  };

  const handleKnowledgeBaseFileDrop = (e) => {
    e.preventDefault();
    if (!e.dataTransfer?.files?.length) return;
    handleKnowledgeBaseFileChange({ target: { files: e.dataTransfer.files } });
  };

  const handleRemoveKnowledgeBaseFile = (indexToRemove) => {
    setKnowledgeBaseFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    setKnowledgeBaseFileError('');
  };

  const isKnowledgeBaseLimitReached = knowledgeBaseFiles.length >= MAX_KNOWLEDGE_BASE_FILES;

  const validateForm = () => {
    const newErrors = {};
    const currentOption = isSmart ? activeTab === 'tab1' ? 'option1' : 'option2' : selectedOption;
    if (currentOption === 'option1') {
      if (!formData.keyword.trim()) newErrors.keyword = t('Le mot clé est obligatoire');
      if (formData.url.trim() && !isValidUrl(formData.url)) newErrors.url = t("Format d'URL invalide");
    } else if (currentOption === 'option2') {
      if (isTranslationProject) {
        if (!selectedFiles.length) {
          newErrors.file = t('Veuillez sélectionner au moins un fichier Word');
        }
      } else if (!selectedFile) {
        newErrors.file = t('Veuillez sélectionner un fichier Excel');
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    const currentOption = isSmart ? activeTab === 'tab1' ? 'option1' : 'option2' : selectedOption;
    if (currentOption === 'option1') {
      if (isTranslationProject) {
        return formData.keyword.trim() && (!formData.url.trim() || isValidUrl(formData.url));
      }
      return (
        formData.keyword.trim() &&
        (!formData.url.trim() || isValidUrl(formData.url))
      );
    } else if (currentOption === 'option2') {
      if (isTranslationProject) {
        return selectedFiles.length > 0;
      }
      return selectedFile !== null;
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const canCreatePremiumContent = await checkPremiumWebsiteQuota();
    if (!canCreatePremiumContent) {
      handleModalClose();
      setIsContentLimitModalOpen(true);
      return;
    }

    if (remainingCredits !== null && remainingCredits <= 0) {
      handleModalClose();
      setIsCreditsLimitModalOpen(true);
      return;
    }

    if (!validateForm()) {
      setAlertMessage(t('Veuillez corriger les erreurs dans le formulaire'));
      setAlertType('danger');
      return;
    }
    setIsLoading(true);
    setAlertMessage('');
    try {
      if (selectedOption === 'option1') {
        await handleOption1Submit();
      } else if (selectedOption === 'option2') {
        await handleOption2Submit();
      }
    } catch (error) {
      const status = (error && error.response && error.response.status) ? error.response.status : null;
      if (status === 406) {
        setAlertMessage(t('Vous avez atteint la limite de génération de texte par IA !'));
      } else {
        const apiMessage = error && error.response && error.response.data && error.response.data.message ? error.response.data.message : null;
        setAlertMessage(apiMessage || error.message || t('Erreur lors de la soumission'));
      }
      setAlertType('danger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOption1Submit = async () => {
    if (isSmart && isTranslationProject) {
      const requestBody = {
        keyword: formData.keyword,
        agentId: aiAgentId || '',
        urlPage: formData.url || undefined,
        originalText: formData.textToTranslate || undefined,
      };
      const formDataToSend = buildUnassignedGenerationFormData(
        requestBody,
        knowledgeBaseFiles
      );
      await axiosPrivate.post(
        `/project/generation/translate-one-text/${orderId}`,
        formDataToSend,
        getMultipartRequestConfig()
      );
      setAlertMessage(t('Génération en cours...'));
      setAlertType('info');
      setNotificationMessage(t('Génération en cours...'));
      startPolling();
      resetForm();
      onClose();
      return;
    }

    const gap = formData.semanticGap && formData.semanticGap.trim()
      ? transformToSemanticGap(formData.semanticGap)
      : [];
    if (isSmart) {
      const gapWords = Array.isArray(gap)
        ? gap
          .map((g) => {
            if (typeof g === 'string') return g;
            const word = (g?.word || '').toString().trim();
            const freq = Number(g?.frequency ?? 1);
            return word ? `${word}(${isNaN(freq) ? 1 : freq})` : '';
          })
          .map((s) => (s || '').trim())
          .filter(Boolean)
        : [];
      const maillageInterne = (formData.internalLinking || '')
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const requestBody = {
        keyword: formData.keyword,
        urlPage: formData.url || undefined,
        gap: gapWords.length > 0 ? gapWords : [],
        maillageInterne: maillageInterne.length > 0 ? maillageInterne : undefined,
        axeRedaction: formData.writingAxis || undefined,
      };

      const formDataToSend = buildUnassignedGenerationFormData(
        requestBody,
        knowledgeBaseFiles
      );
      await axiosPrivate.post(
        `/project/generation/generate-one-text/${orderId}`,
        formDataToSend,
        getMultipartRequestConfig()
      );

      setAlertMessage(t('Génération en cours...'));
      setAlertType('info');
      setNotificationMessage(t('Génération en cours...'));
      startPolling();
      resetForm();
      onClose();
    } else {
      await createRedactionAndRedirect(gap);
    }
  };

  const handleOption2Submit = async () => {
    if (isTranslationProject) {
      if (orderTextAmount && selectedFiles.length > 0) {
        const remainingTexts = orderTextAmount - currentGeneratedTextsCount;
        if (selectedFiles.length > remainingTexts) {
          setAlertMessage(t(`Vous avez atteint la limite de textes pour ce projet. Il ne reste que ${remainingTexts} texte(s) à générer.`));
          setAlertType('danger');
          return;
        }
      }

      setAlertMessage(t('Traitement des fichiers Word en cours...'));
      setAlertType('info');
      setNotificationMessage(t('Génération en cours...'));

      const formDataToSend = new FormData();
      selectedFiles.forEach((file) => {
        formDataToSend.append('files', file);
      });

      await axiosPrivate.post(`/project/generation/translate-batch-text/${orderId}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setAlertMessage(t('Génération en cours... Surveillance des jobs.'));
      setAlertType('info');
      startPolling();
      resetForm();
      onClose();
      return;
    }

    if (orderTextAmount && filePreviewData.length > 0) {
      const remainingTexts = orderTextAmount - currentGeneratedTextsCount;
      if (filePreviewData.length > remainingTexts) {
        setAlertMessage(t(`Vous avez atteint la limite de textes pour ce projet. Il ne reste que ${remainingTexts} texte(s) à générer.`));
        setAlertType('danger');
        return;
      }
    }

    setAlertMessage(t('Traitement du fichier Excel en cours...'));
    setAlertType('info');
    setNotificationMessage(t('Génération en cours...'));

    const formDataToSend = new FormData();
    formDataToSend.append('excelFile', selectedFile);

    await axiosPrivate.post(`/project/generation/generate-batch-text/${orderId}`, formDataToSend, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    setAlertMessage(t('Génération en cours... Surveillance des jobs.'));
    setAlertType('info');
    startPolling();
    resetForm();
    onClose();
  };

  const createRedactionAndRedirect = async (gap) => {
    const initialContent = '';
    const isTranslationOrder =
      translationIndicators.has(normalizedOrderType) ||
      translationIndicators.has(normalizedOrderTextType);

    const redactionType = formData.type
      ? formData.type.toUpperCase()
      : isTranslationOrder
        ? 'TRANSLATED'
        : 'NON_TRANSLATED';

    try {
      window.localStorage.setItem('WriterClient_PendingTextType', redactionType);
    } catch { }

    const newRedactionData = {
      keyword: formData.keyword,
      url: formData.url,
      keywords: gap,
      semanticGap: gap,
      internalLinking: formData.internalLinking,
      writingAxis: formData.writingAxis,
      orderId: orderId,
      title: formData.keyword,
      status: 'CREATED',
      createdAt: new Date().toISOString(),
      actualTextContent: initialContent,
      actualHtmlContent: initialContent,
      textTitle: formData.keyword,
      titleTag: '',
      metaDescriptionTag: '',
      stuffedKeywords: [],
      analyseResult: null,
      initialScoreData: {
        seoScore: 0,
        metrics: {
          semanticResult: {
            title: t('Résultat sémantique'),
            score: 0,
            data: { wordsInBold: [] },
            infoTooltip: t('Score sémantique du contenu')
          },
          tags: {
            title: t('Balises'),
            score: 0,
            data: {},
            infoTooltip: t('Optimisation des balises HTML')
          },
          keywordStuffing: {
            title: t('Keyword Stuffing'),
            score: 0,
            data: {},
            infoTooltip: t('Détection du bourrage de mots-clés')
          },
          internalLinking: {
            title: t('Maillage interne'),
            score: 0,
            data: {},
            infoTooltip: t('Optimisation des liens internes')
          }
        }
      },
      type: isTranslationOrder ? 'TRANSLATED' : 'NON_TRANSLATED',
      textType: isTranslationOrder ? 'TRANSLATED' : 'NON_TRANSLATED',
    };
    dispatch(addRedaction(newRedactionData));
    dispatch(setCurrentRedaction(newRedactionData));
    onClose();
    setIsReadyToRedirect(true);
    resetForm();
  };

  const resetForm = () => {
    setFormData({ keyword: '', url: '', semanticGap: '', internalLinking: '', writingAxis: '', type: '', textToTranslate: '' });
    setErrors({});
    setAlertMessage('');
    setSelectedFile(null);
    setSelectedFiles([]);
    setSelectedOption('option1');
    setFilePreviewData([]);
    setCurrentPage(1);
    setCurrentGeneratedTextsCount(0);
    setIsKnowledgeBaseSectionOpen(false);
    setKnowledgeBaseFiles([]);
    setKnowledgeBaseFileError('');
    const fileInput = document.getElementById('smartBatchUpload');
    if (fileInput) fileInput.value = '';
    const kbInput = document.getElementById('redaction-kb-upload-input');
    if (kbInput) kbInput.value = '';
  };

  const handleKeywordsGenerated = () => {
    setFormData((prev) => ({
      ...prev,
      semanticGap: combinedKeywords || prev.semanticGap,
      internalLinking: websites || prev.internalLinking,
    }));
    if (isSuccess && errorSuccessMsg) {
      setAlertMessage(errorSuccessMsg);
      setAlertType('success');
    } else if (!isSuccess && errorSuccessMsg) {
      setAlertMessage(errorSuccessMsg);
      setAlertType('danger');
    }
    setErrorSuccess(false);
    setErrorSuccessMsg('');
    setIsSuccess(false);
  };

  React.useEffect(() => {
    if (errorSuccess) {
      handleKeywordsGenerated();
    }
  }, [errorSuccess, isSuccess, errorSuccessMsg, combinedKeywords, websites]);

  const handleModalClose = () => {
    if (isLoading) return;
    resetForm();
    setKeywords('');
    setCombinedKeywords('');
    setCombinedKeywordsList([]);
    setWebsites('');
    setErrorSuccessMsg('');
    setIsSuccess(false);
    setErrorSuccess(false);
    onClose();
  };

  const handleSemanticGapChangeFromModal = (event) => {
    const value = event?.target?.value || '';
    setFormData((prev) => ({ ...prev, semanticGap: value }));
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={handleModalClose}
        size="xl"
        className="redaction-modal"
        backdrop={isLoading || isRetrieving ? 'static' : true}
        keyboard={!isLoading && !isRetrieving}
      >
        <ModalHeader toggle={!isLoading && !isRetrieving ? handleModalClose : undefined}>
          {title ? title : t('Créer un nouveau contenu')}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            {alertMessage && (
              <Alert color={alertType} className="mb-3">
                {alertMessage}
              </Alert>
            )}
            {isSmart && (
              <>
                <div className="modal-option-row">
                  <button
                    type="button"
                    className={`option-card ${activeTab === 'tab1' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveTab('tab1');
                      setSelectedOption('option1');
                      setSelectedFile(null);
                      setSelectedFiles([]);
                      setFilePreviewData([]);
                      setCurrentPage(1);
                      const fileInput = document.getElementById('smartBatchUpload');
                      if (fileInput) fileInput.value = '';
                    }}
                  >
                    <img
                      src={activeTab === 'tab1' ? 'Images/one-text-acti.png' : 'Images/one-text-desact.png'}
                      alt="One text icon"
                      style={{ width: '40px', height: '40px', marginRight: '12px' }}
                    />
                    <div className="option-text">
                      <div
                        className="option-title"
                        style={{
                          marginLeft: '-2px',
                          color: '#111827',
                          marginLeft: isTranslationProject ? '-70px' : '0px',
                        }}
                      >
                        {isTranslationProject ? t('Traduire un contenu') : t('Rédiger un contenu')}
                      </div>
                      <div
                        className="option-subtitle"
                        style={{
                          marginLeft: '-1px',
                          color: '#6b7280',
                          marginLeft: isTranslationProject ? '-70px' : '0px',
                        }}
                      >
                        {isTranslationProject
                          ? t('Traduire un texte spécifique avec un formulaire guidé')
                          : t('Créez un texte pour un mot-clé spécifique avec un formulaire guidé')}
                      </div>
                    </div>
                    <div className={`option-check ${activeTab === 'tab1' ? 'checked' : ''}`} />
                  </button>

                  <button
                    type="button"
                    className={`option-card ${activeTab === 'tab2' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveTab('tab2');
                      setSelectedOption('option2');
                    }}
                  >
                    <img
                      src={activeTab === 'tab2' ? 'Images/act-batch-text.png' : 'Images/batch-icon-desact.png'}
                      alt="Batch icon"
                      style={{ width: '40px', height: '40px',   marginRight: isTranslationProject ? '-10px' : '-50px', }}
                    />
                    <div className="option-text">
                      <div className="option-title" id='option-title-redac'>
                        {isTranslationProject ? t('Traduire un lot de contenus') : t('Rédiger un lot de contenus')}
                      </div>
                      <div className="option-subtitle" id='option-subtitle-redac'>
                        {isTranslationProject
                          ? t('Traduisez plusieurs contenus en important des fichiers Word')
                          : t('Générez plusieurs textes en important un fichier Excel')}
                      </div>
                    </div>
                    <div className={`option-check ${activeTab === 'tab2' ? 'checked' : ''}`} />
                  </button>
                </div>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="tab1">
                    <FormGroup>
                      <Label for="keyword" className="required-field">
                        {t('Mot clé')}
                      </Label>
                      <Input
                        type="text"
                        id="keyword"
                        name="keyword"
                        value={formData.keyword}
                        onChange={handleInputChange}
                        placeholder={t('Ex: télépéage automatique')}
                        disabled={isLoading || isRetrieving || activeTab !== 'tab1'}
                        invalid={!!errors.keyword}
                      />
                      {errors.keyword && <div className="invalid-feedback d-block">{errors.keyword}</div>}
                    </FormGroup>

                    <FormGroup>
                      <Label for="url">{t('Url page')}</Label>
                      <Input
                        type="text"
                        id="url"
                        name="url"
                        value={formData.url}
                        onChange={handleInputChange}
                        placeholder={t('www.exemple.com/article')}
                        disabled={isLoading || isRetrieving || activeTab !== 'tab1'}
                        invalid={!!errors.url}
                      />
                      {errors.url && <div className="invalid-feedback d-block">{errors.url}</div>}
                    </FormGroup>

                    {isTranslationProject ? (
                      <>
                      <FormGroup>
                        <Label for="textToTranslate">
                          {t('Texte à traduire')}
                        </Label>
                        <Input
                          type="textarea"
                          id="textToTranslate"
                          name="textToTranslate"
                          value={formData.textToTranslate}
                          onChange={handleInputChange}
                          placeholder={t('Saisissez le texte à traduire')}
                          disabled={isLoading || isRetrieving || activeTab !== 'tab1'}
                          rows="8"
                        />
                        {errors.textToTranslate && <div className="invalid-feedback d-block">{errors.textToTranslate}</div>}
                      </FormGroup>

                        <div className="redaction-kb-section">
                          <h6
                            className="redaction-kb-section-title"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              cursor: 'pointer',
                            }}
                            onClick={() => setIsKnowledgeBaseSectionOpen((prev) => !prev)}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <img
                                src="/Images/conai-icon.png"
                                alt="info"
                                className="redaction-kb-section-icon"
                              />
                              {t('Section 2 — Base de connaissance (optionnel)')}
                            </span>
                            <span
                              style={{
                                display: 'inline-block',
                                transition: 'transform 0.2s ease',
                                transform: isKnowledgeBaseSectionOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                fontSize: 14,
                                color: '#818181',
                              }}
                            >
                              ▼
                            </span>
                          </h6>

                          {isKnowledgeBaseSectionOpen && (
                            <>
                              <p className="redaction-kb-section-description">
                                {t("Optionnel : vos ressources internes aident l’agent à produire des contenus plus précis et personnalisés.")}
                              </p>
                              <div
                                className={`redaction-kb-upload-dropzone${isKnowledgeBaseLimitReached ? ' is-limit-reached' : ''}`}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={handleKnowledgeBaseFileDrop}
                              >
                                <input
                                  type="file"
                                  id="redaction-translate-kb-upload-input"
                                  multiple
                                  accept={KNOWLEDGE_BASE_ACCEPT}
                                  disabled={isKnowledgeBaseLimitReached || isLoading || isRetrieving}
                                  style={{ display: 'none' }}
                                  onChange={handleKnowledgeBaseFileChange}
                                />
                                <div className="redaction-kb-upload-dropzone-inner">
                                  <div className="redaction-kb-upload-dropzone-icon">
                                    <img src="/Images/imp-icon.png" alt="Upload" />
                                  </div>
                                  <div
                                    className={`redaction-kb-upload-dropzone-text-main${isKnowledgeBaseLimitReached ? ' is-limit-message' : ''}`}
                                  >
                                    {isKnowledgeBaseLimitReached
                                      ? t('Vous avez atteint le nombre maximum de fichiers autorisés pour cet agent.')
                                      : t('Glissez-déposez votre fichier')}
                                  </div>
                                  {!isKnowledgeBaseLimitReached && (
                                    <div className="redaction-kb-upload-dropzone-text-sub">
                                      {t('ou cliquez pour parcourir')}
                                      {' — '}
                                      {t('Formats acceptés : PDF, DOCX, TXT, MD, CSV.')}
                                    </div>
                                  )}
                                  <label
                                    htmlFor={isKnowledgeBaseLimitReached ? undefined : 'redaction-translate-kb-upload-input'}
                                    className={`redaction-kb-upload-dropzone-button${isKnowledgeBaseLimitReached ? ' is-disabled' : ''}`}
                                  >
                                    {t('Parcourir les fichiers')}
                                  </label>
                                  {knowledgeBaseFiles.length > 0 && (
                                    <div className="redaction-kb-upload-dropzone-filelist">
                                      {knowledgeBaseFiles.map((file, idx) => (
                                        <div
                                          key={`${file.name}-${idx}`}
                                          className="redaction-kb-upload-dropzone-filename-row"
                                        >
                                          <span
                                            className="redaction-kb-upload-dropzone-filename"
                                            title={file.name}
                                          >
                                            {file.name}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRemoveKnowledgeBaseFile(idx);
                                            }}
                                            className="redaction-kb-upload-dropzone-remove"
                                            aria-label={t('Supprimer le fichier')}
                                          >
                                            <img
                                              src="/Images/close-iconnn.png"
                                              alt={t('Supprimer')}
                                              style={{ width: 14, height: 14 }}
                                            />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {knowledgeBaseFileError ? (
                                <div className="redaction-kb-upload-dropzone-error">
                                  {knowledgeBaseFileError}
                                </div>
                              ) : null}
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <FormGroup>
                          <Label for="semanticGap">
                            {t('GAP sémantique')}
                            <span id="gapTooltipRedac" className="ms-2" style={{ cursor: 'pointer', color: '#ef4444' }}>ⓘ</span>
                          </Label>
                          <Input
                            type="textarea"
                            id="semanticGap"
                            name="semanticGap"
                            value={formData.semanticGap}
                            onChange={handleInputChange}
                            placeholder={t('keyword1 (2),\nkeyword2 (1),\nkeyword3 (1),\nkeyword4 (2),\n.....')}
                            disabled={isLoading || isRetrieving || activeTab !== 'tab1'}
                            rows="4"
                            className="semantic-gap-textarea"
                          />
                          {errors.semanticGap && (
                            <div className="invalid-feedback d-block">{errors.semanticGap}</div>
                          )}
                          <UncontrolledTooltip
                            placement="right"
                            target="gapTooltipRedac"
                            className="custom-redac-tooltip"
                          >
                            {t('Saisissez chaque mot-clé suivi de sa fréquence entre parenthèses, séparés par une virgule ou un retour à la ligne. \n Exemple :\n chaussures(3), robe(2), pantalon(1)')}
                          </UncontrolledTooltip>

                          <div
                            className="semantic-gap-actions"
                            style={{ marginLeft: '150px' }}
                          >
                            <button
                              type="button"
                              className="gap-light-btn"
                              onClick={() => {
                                if (gapAnalysisEnabled !== true) return;
                                setIsGapModalOpen(true);
                              }}
                              disabled={gapAnalysisEnabled !== true || isLoading || isRetrieving}
                            >
                              <span className="gap-refresh-icon">
                                <img
                                  src="/Images/gap-icon-gene.png"
                                  alt={t('Générer une couverture sémantique')}
                                  style={{ width: 16, height: 16, marginLeft: -6 }}
                                />
                              </span>
                              <span className="gap-refresh-label">
                                {t('Générer une couverture sémantique')}
                              </span>
                            </button>
                          </div>
                        </FormGroup>

                        <FormGroup>
                          <Label for="internalLinking">
                            {t('Maillage interne')}
                          </Label>
                          <Input
                            type="textarea"
                            id="internalLinking"
                            name="internalLinking"
                            value={formData.internalLinking}
                            onChange={handleInputChange}
                            placeholder={t('URLs des pages à lier (séparées par des virgules)')}
                            rows="3"
                            disabled={isLoading || activeTab !== 'tab1'}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label for="writingAxis">
                            {t('Axe de rédaction')}
                          </Label>
                          <Input
                            type="textarea"
                            id="writingAxis"
                            name="writingAxis"
                            value={formData.writingAxis}
                            onChange={handleInputChange}
                            placeholder={t('Indiquez un angle éditorial (optionnel)')}
                            rows="3"
                            disabled={isLoading || activeTab !== 'tab1'}
                          />
                        </FormGroup>

                        <div className="redaction-kb-section">
                          <h6
                            className="redaction-kb-section-title"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              cursor: 'pointer',
                            }}
                            onClick={() => setIsKnowledgeBaseSectionOpen((prev) => !prev)}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <img
                                src="/Images/conai-icon.png"
                                alt="info"
                                className="redaction-kb-section-icon"
                              />
                              {t('Section 2 — Base de connaissance (optionnel)')}
                            </span>
                            <span
                              style={{
                                display: 'inline-block',
                                transition: 'transform 0.2s ease',
                                transform: isKnowledgeBaseSectionOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                fontSize: 14,
                                color: '#818181',
                              }}
                            >
                              ▼
                            </span>
                          </h6>

                          {isKnowledgeBaseSectionOpen && (
                            <>
                              <p className="redaction-kb-section-description">
                                {t("Optionnel : vos ressources internes aident l’agent à produire des contenus plus précis et personnalisés.")}
                              </p>
                              <div
                                className={`redaction-kb-upload-dropzone${isKnowledgeBaseLimitReached ? ' is-limit-reached' : ''}`}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={handleKnowledgeBaseFileDrop}
                              >
                                <input
                                  type="file"
                                  id="redaction-kb-upload-input"
                                  multiple
                                  accept={KNOWLEDGE_BASE_ACCEPT}
                                  disabled={isKnowledgeBaseLimitReached || isLoading || isRetrieving}
                                  style={{ display: 'none' }}
                                  onChange={handleKnowledgeBaseFileChange}
                                />
                                <div className="redaction-kb-upload-dropzone-inner">
                                  <div className="redaction-kb-upload-dropzone-icon">
                                    <img src="/Images/imp-icon.png" alt="Upload" />
                                  </div>
                                  <div
                                    className={`redaction-kb-upload-dropzone-text-main${isKnowledgeBaseLimitReached ? ' is-limit-message' : ''}`}
                                  >
                                    {isKnowledgeBaseLimitReached
                                      ? t('Vous avez atteint le nombre maximum de fichiers autorisés pour cet agent.')
                                      : t('Glissez-déposez votre fichier')}
                                  </div>
                                  {!isKnowledgeBaseLimitReached && (
                                    <div className="redaction-kb-upload-dropzone-text-sub">
                                      {t('ou cliquez pour parcourir')}
                                      {' — '}
                                      {t('Formats acceptés : PDF, DOCX, TXT, MD, CSV.')}
                                    </div>
                                  )}
                                  <label
                                    htmlFor={isKnowledgeBaseLimitReached ? undefined : 'redaction-kb-upload-input'}
                                    className={`redaction-kb-upload-dropzone-button${isKnowledgeBaseLimitReached ? ' is-disabled' : ''}`}
                                  >
                                    {t('Parcourir les fichiers')}
                                  </label>
                                  {knowledgeBaseFiles.length > 0 && (
                                    <div className="redaction-kb-upload-dropzone-filelist">
                                      {knowledgeBaseFiles.map((file, idx) => (
                                        <div
                                          key={`${file.name}-${idx}`}
                                          className="redaction-kb-upload-dropzone-filename-row"
                                        >
                                          <span
                                            className="redaction-kb-upload-dropzone-filename"
                                            title={file.name}
                                          >
                                            {file.name}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRemoveKnowledgeBaseFile(idx);
                                            }}
                                            className="redaction-kb-upload-dropzone-remove"
                                            aria-label={t('Supprimer le fichier')}
                                          >
                                            <img
                                              src="/Images/close-iconnn.png"
                                              alt={t('Supprimer')}
                                              style={{ width: 14, height: 14 }}
                                            />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {knowledgeBaseFileError ? (
                                <div className="redaction-kb-upload-dropzone-error">
                                  {knowledgeBaseFileError}
                                </div>
                              ) : null}
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </TabPane>

                  <TabPane tabId="tab2">
                    <div className="batch-upload-container">
                      {!isTranslationProject && (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '20px' }}>
                            <img
                              src="Images/icon-excel-import.png"
                              alt="excel"
                              style={{ width: '40px', height: '40px', marginTop: '20px' }}
                            />
                            <p style={{ fontSize: '14px', marginTop: '30px' }}>
                              {t("Besoin d'aide pour préparer votre fichier ?")}
                            </p>
                          </div>

                          <div className="d-flex align-items-center mb-3" style={{ gap: '12px' }}>
                            <label
                              htmlFor="smartBatchUpload"
                              className={`upload-button ${selectedFile ? 'file-selected' : ''}`}
                            />
                            <span
                              onClick={handleDownloadTemplate}
                              role="button"
                              style={{ cursor: 'pointer' }}
                              title={t('Télécharger le modèle (Excel)')}
                            >
                              <button
                                type="button"
                                style={{
                                  backgroundColor: '#FDFDFC',
                                  color: '#2F979E',
                                  border: '2px solid #2F979E',
                                  height: '40px',
                                  width: '250px',
                                  borderRadius: '8px',
                                  marginLeft: '55px',
                                }}
                              >
                                {t('Télécharger le modèle (Excel)')}
                              </button>
                            </span>
                          </div>
                        </>
                      )}

                      <input
                        id="smartBatchUpload"
                        type="file"
                        accept={isTranslationProject ? '.doc,.docx' : '.csv,.xlsx,.xls'}
                        multiple={isTranslationProject}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                        disabled={isLoading || isRetrieving}
                      />

                      {errors.file && (
                        <div className="text-danger mt-2" style={{ fontSize: '0.875rem' }}>
                          {errors.file}
                        </div>
                      )}
                    </div>

                    {!(isTranslationProject ? selectedFiles.length : selectedFile) ? (
                      <div
                        className={`batch1-upload-container ${isDragging ? 'dragging' : ''}`}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        style={{
                          border: isDragging ? '2px dashed #2F979E' : '2px dashed #E0E0E0',
                          backgroundColor: isDragging ? '#f0f9fa' : '#FAFAFA',
                          borderRadius: '12px',
                          padding: '40px',
                          textAlign: 'center',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <div>
                          <img
                            src="Images/batch-icon-desact.png"
                            alt="Upload icon"
                            style={{
                              width: '60px',
                              height: '60px',
                              marginBottom: '20px',
                              borderRadius: '50%',
                              opacity: isDragging ? 0.7 : 1
                            }}
                          />
                          <p style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            marginBottom: '10px',
                            color: isDragging ? '#2F979E' : '#000'
                          }}>
                            {isDragging
                              ? t('Déposez vos fichiers ici')
                              : isTranslationProject
                                ? t('Glissez-déposez un ou plusieurs fichier word.')
                                : t('Glissez-déposez votre fichier Excel')}
                          </p>
                          <p style={{ fontSize: '14px', color: '#616161', marginBottom: '20px' }}>
                            {t('ou cliquez pour parcourir')}
                          </p>
                          <button
                            type="button"
                            onClick={() => document.getElementById('smartBatchUpload').click()}
                            disabled={isLoading || isRetrieving}
                            style={{
                              height: '40px',
                              width: '170px',
                              borderRadius: '8px',
                              backgroundColor: '#FFFFFF',
                              color: 'black',
                              border: '1px solid #CFCFCF',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}
                          >
                            {t('Parcourir les fichiers')}
                          </button>
                          <p style={{ fontSize: '12px', color: '#616161', marginTop: '15px' }}>
                            {isTranslationProject
                              ? t('Format accepté : .doc, docx')
                              : t('Formats acceptés : .xlsx, .xls (max 10 Mo)')}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="file-selected-container"
                        style={{
                          border: '1px solid #E0E0E0',
                          borderRadius: '12px',
                          padding: '20px',
                          backgroundColor: '#FAFAFA',
                          marginTop: '20px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <div style={{ marginTop: '-10px' }}>
                              <img
                                src="Images/icon-succ.png"
                                alt="Excel file"
                                style={{ width: '40px', height: '40px', marginTop: '-120px' }}
                              />
                            </div>
                            <div>
                              <p style={{ fontSize: '14px', fontWeight: '600', margin: 0, color: '#000' }}>
                                {isTranslationProject
                                  ? `${selectedFiles.length} ${t('fichier(s) sélectionné(s)')}`
                                  : selectedFile.name}
                              </p>
                              <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'left', gap: '5px' }}>
                                <img src="Images/check-icon.png" alt="check" style={{ width: '18px', height: '18px', marginTop: '5px' }} />
                                <p style={{ fontSize: '13px', fontWeight: '500', color: '#16A34A', margin: 0, marginTop: '4px' }}>
                                  {isTranslationProject
                                    ? `${selectedFiles.length} ${t('fichier(s) prêt(s) pour la traduction')}`
                                    : `${filePreviewData?.length || 0} ${t('contenus détectés')}`}
                                </p>
                              </div>
                              {isTranslationProject && (
                                <ul style={{ listStyle: 'none', marginTop: '10px', padding: 0, marginBottom: 0 }}>
                                  {selectedFiles.map((file) => (
                                    <li
                                      key={`${file.name}-${file.lastModified}`}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '4px 0',
                                        fontSize: '13px',
                                        color: '#374151',
                                      }}
                                    >
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {file.name}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveWordFile(file)}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: '#616161',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          lineHeight: 1,
                                          padding: 0,
                                          flexShrink: 0,
                                        }}
                                        title={t('Supprimer le fichier')}
                                      >
                                        ✕
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              <p className='text-success1'>
                                {isTranslationProject
                                  ? t('Vos fichiers Word sont prêts. Cliquez sur "Lancer la traduction" pour lancer la traduction des contenus.')
                                  : t('Votre fichier a été analysé avec succès. Cliquez sur "Lancer la rédaction" pour lancer la génération des contenus.')}
                              </p>
                              {!isTranslationProject && (
                                <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'left', marginTop: '10px', gap: '8px' }}>
                                  <img src="Images/imp-icon.png" alt="import" style={{ width: '20px', height: '20px', marginTop: '20px' }} />
                                  <button
                                    type="button"
                                    onClick={handleRemoveFile}
                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginTop: '22.5px' }}
                                  >
                                    {t('Changer de fichier')}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleRemoveFile}
                            className='close-button1'
                            style={{ background: 'none', border: 'none', color: '#616161', cursor: 'pointer', fontSize: '12px' }}
                            title={t('Supprimer le fichier')}
                          >
                            ✕
                          </button>
                        </div>
                        {isTranslationProject && (
                          <div
                            className={`batch1-upload-container ${isDragging ? 'dragging' : ''}`}
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            style={{
                              border: isDragging ? '2px dashed #2F979E' : '2px dashed #E0E0E0',
                              backgroundColor: isDragging ? '#f0f9fa' : '#FAFAFA',
                              borderRadius: '12px',
                              padding: '40px',
                              textAlign: 'center',
                              marginTop: '16px',
                              transition: 'all 0.3s ease',
                            }}
                          >
                            <div>
                              <img
                                src="Images/batch-icon-desact.png"
                                alt="Upload icon"
                                style={{
                                  width: '60px',
                                  height: '60px',
                                  marginBottom: '20px',
                                  borderRadius: '50%',
                                  opacity: isDragging ? 0.7 : 1,
                                }}
                              />
                              <p style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                marginBottom: '10px',
                                color: isDragging ? '#2F979E' : '#000',
                              }}>
                                {isDragging
                                  ? t('Déposez vos fichiers ici')
                                  : t('Charger d\'autres fichiers')}
                              </p>
                              <p style={{ fontSize: '14px', color: '#616161', marginBottom: '20px' }}>
                                {t('ou cliquez pour parcourir')}
                              </p>
                              <button
                                type="button"
                                onClick={() => document.getElementById('smartBatchUpload').click()}
                                disabled={isLoading || isRetrieving}
                                style={{
                                  height: '40px',
                                  width: '170px',
                                  borderRadius: '8px',
                                  backgroundColor: '#FFFFFF',
                                  color: 'black',
                                  border: '1px solid #CFCFCF',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                }}
                              >
                                {t('Parcourir les fichiers')}
                              </button>
                              <p style={{ fontSize: '12px', color: '#616161', marginTop: '15px' }}>
                                {t('Format accepté : .doc, docx')}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </TabPane>
                </TabContent>
              </>
            )}

            {!isSmart && (
              <>
                <FormGroup>
                  <Label for="keyword" className="required-field">
                    {t('Mots clé')}
                  </Label>
                  <Input
                    type="text"
                    id="keyword"
                    name="keyword"
                    value={formData.keyword}
                    onChange={handleInputChange}
                    placeholder={t('Entrez le mot clé principal')}
                    disabled={isLoading || isRetrieving}
                    invalid={!!errors.keyword}
                  />
                  {errors.keyword && <div className="invalid-feedback d-block">{errors.keyword}</div>}
                </FormGroup>
                <FormGroup>
                  <Label for="url">{t('Url de la page')}</Label>
                  <Input
                    type="text"
                    id="url"
                    name="url"
                    value={formData.url}
                    onChange={handleInputChange}
                    placeholder={t('Entrez l url')}
                    disabled={isLoading || isRetrieving}
                    invalid={!!errors.url}
                  />
                  {errors.url && <div className="invalid-feedback d-block">{errors.url}</div>}
                </FormGroup>
                <FormGroup>
                  <Label for="semanticGap">
                    {t('GAP sémantique')}
                    <span id="gapTooltipPremium" className="ms-2" style={{ cursor: 'pointer', color: '#ef4444' }}>ⓘ</span>
                  </Label>
                  <Input
                    type="textarea"
                    id="semanticGap"
                    name="semanticGap"
                    value={formData.semanticGap}
                    onChange={handleInputChange}
                    placeholder={t('Ex: voiture(5), électrique(3), autonomie(8)')}
                    disabled={isLoading || isRetrieving}
                    rows="8"
                    className="semantic-gap-textarea"
                  />
                  <UncontrolledTooltip
                    placement="right"
                    target="gapTooltipPremium"
                    className="custom-redac-tooltip"
                  >
                    {t('Saisissez chaque mot-clé suivi de sa fréquence entre parenthèses, séparés par une virgule ou un retour à la ligne. \n Exemple :\n chaussures(3), robe(2), pantalon(1)')}
                  </UncontrolledTooltip>
                  <p style={{ fontSize: '12px', color: '#616161', marginTop: '4px' }}>
                    {t('Saisissez chaque mot-clé suivi de sa fréquence entre parenthèses, séparés par une virgule ou un retour à la ligne. Exemple : chaussures(3), robe(2), pantalon(1)')}
                  </p>
                  {errors.semanticGap && <div className="invalid-feedback d-block">{errors.semanticGap}</div>}

                  {/* ✅ Bouton "Générer une couverture sémantique" ajouté dans le popin premium */}
                  <div className="semantic-gap-actions" style={{ marginLeft: '150px' }}>
                    <button
                      type="button"
                      className="gap-light-btn"
                      onClick={() => {
                        if (gapAnalysisEnabled !== true) return;
                        setIsGapModalOpen(true);
                      }}
                      disabled={gapAnalysisEnabled !== true || isLoading || isRetrieving}
                    >
                      <span className="gap-refresh-icon">
                        <img
                          src="/Images/gap-icon-gene.png"
                          alt={t('Générer une couverture sémantique')}
                          style={{ width: 16, height: 16, marginLeft: -6 }}
                        />
                      </span>
                      <span className="gap-refresh-label">
                        {t('Générer une couverture sémantique')}
                      </span>
                    </button>
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label for="internalLinking">
                    {t('Maillage interne')}
                  </Label>
                  <Input
                    type="textarea"
                    id="internalLinking"
                    name="internalLinking"
                    value={formData.internalLinking}
                    onChange={handleInputChange}
                    placeholder={t('Introduisez le maillage interne')}
                    rows="3"
                    disabled={isLoading || isRetrieving}
                  />
                </FormGroup>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <button
              type="button"
              onClick={handleModalClose}
              disabled={isLoading || isRetrieving}
              style={{
                backgroundColor: '#FFFFFF',
                color: '#000000',
                border: '1px solid #CFCFCF',
                borderRadius: '8px',
                padding: '10px 24px',
                fontWeight: '500',
                cursor: 'pointer',
                fontSize: '14px',
                lineHeight: '1.5',
                transition: 'all 0.15s ease-in-out'
              }}
            >
              {t('Annuler')}
            </button>
            <button
              type="submit"
              disabled={isLoading || isRetrieving || !isFormValid()}
              style={{
                backgroundColor: '#2F979E',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 24px',
                fontWeight: '500',
                cursor: isLoading || isRetrieving || !isFormValid() ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                lineHeight: '1.5',
                opacity: isLoading || isRetrieving || !isFormValid() ? 0.6 : 1,
                transition: 'all 0.15s ease-in-out'
              }}
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  {selectedOption === 'option1' ? t('Traitement...') : t('Traitement du fichier...')}
                </>
              ) : (
                isTranslationProject
                  ? (activeTab === 'tab1' ? t('Commencer la traduction') : t('Lancer la traduction'))
                  : isSmart
                    ? (activeTab === 'tab1' ? t('Générer le contenu') : t('Lancer la rédaction'))
                    : t('Lancer la création')
              )}
            </button>
          </ModalFooter>
        </Form>

        <GapGenerationModal
          isOpen={isGapModalOpen}
          onClose={() => setIsGapModalOpen(false)}
          mode={gapMode}
          onModeChange={setGapMode}
          keyword={formData.keyword}
          orderWebsite={orderWebsite}
          url={formData.url}
          language={language}
          country={country}
          contentScore={null}
          handleKeywordChange={handleSemanticGapChangeFromModal}
          onUpdate={() => { }}
          disableLightGap={gapAnalysisEnabled !== true}
        />
      </Modal>
      <ProjectCreatedModal
        isOpen={isContentLimitModalOpen}
        onClose={() => setIsContentLimitModalOpen(false)}
        title={t('Limite de contenus atteinte')}
        message={t("Vous avez atteint le nombre maximum de contenus inclus dans votre forfait. Passez à une offre supérieure pour continuer à créer et optimiser vos contenus.")}
        confirmButtonText={t('Passer au forfait supérieur')}
        onConfirm={() => {
          setIsContentLimitModalOpen(false);
          if (!window.location.pathname.startsWith('/Consultant')) {
            history.push('/AdminClient/Abonnement');
          }
        }}
        secondaryButtonText={t('Fermer')}
        onSecondaryAction={() => {
          setIsContentLimitModalOpen(false);
        }}
      />
      <ProjectCreatedModal
        isOpen={isCreditsLimitModalOpen}
        onClose={() => setIsCreditsLimitModalOpen(false)}
        title={t('Limite atteinte')}
        message={t("Vous avez atteint la limite autorisée par votre forfait. Rechargez votre compte dès maintenant pour continuer à générer du contenu, enrichir vos textes et utiliser vos agents sans interruption.")}
        confirmButtonText={t('Acheter des crédits')}
        onConfirm={() => {
          setIsCreditsLimitModalOpen(false);
          setIsBuyCreditsModalOpen(true);
        }}
        secondaryButtonText={t('Passer à une offre supérieure')}
        onSecondaryAction={() => {
          setIsCreditsLimitModalOpen(false);
        }}
      />
      <BuyCreditsModal open={isBuyCreditsModalOpen} onClose={() => setIsBuyCreditsModalOpen(false)} />
    </>
  );
};

export default RedactionModal;
